import type { ReactNode } from "react";

import { FieldNotch } from "@/components/ui/field-notch";
import {
  MUI_FIELD_HELPER_TEXT,
  MUI_FIELD_LABEL_TRANSITION,
  MUI_RESTING_FIELD_LABEL_BOX,
  MUI_RESTING_FIELD_LABEL_BOX_SMALL,
  MUI_SHRUNK_FIELD_LABEL_BOX,
} from "@/components/ui/mui-field-shape";
import { cn } from "@/lib/utils";
import { RequiredAsterisk } from "./required-asterisk";

// The donor's outlined form control, as one shell every ported form field wears.
//
// The old stack builds every form out of MUI's outlined `TextField` and
// `FormControl` + `InputLabel` + `Select` (`InputField`,
// `CustomSelectWithPagination`, `CategoriesFieldPaginated`), and the donor theme
// overrides none of their layout — so **MUI's outlined defaults are the source
// spec** (`docs/agents/migration-guidelines.md`, "Shared donor components port
// as shared Atlas components"). The concrete facts this carries:
//
//   - 56px tall (40px at MUI's `size='small'`, the `size` prop), 1px border
//     `rgba(0, 0, 0, 0.23)`, 4px corner radius;
//   - 16px content text, 14px of horizontal padding;
//   - the label starts *inside* the field at 16px and, once the field holds a
//     value or takes focus, shrinks to 12px and rides up onto the border line;
//   - focus draws the border in the primary colour at 2px; an error draws it in
//     the error colour and turns the label and the helper text with it;
//   - helper text is 12px, 3px below the field and inset 14px.
//
// (`messageSpacing` is the one documented exception to that last line — see the
// prop.)
//
// The **line and its notch** — MUI cuts a gap in the border for the shrunk
// label — are `FieldNotch`, MUI's own `<fieldset>` and `<legend>`, laid inside
// the frame below as MUI lays its fieldset inside `.MuiOutlinedInput-root`. The
// frame keeps every state rule it had and hands the fieldset the result as two
// custom properties; its own file carries why the line is a real cut rather
// than a band painted over a shadow (#846).
//
// It renders the *frame* only. The control inside is the caller's — an `input`,
// a select trigger — which is what lets one shell serve the text fields and both
// ported selects instead of each re-deriving the border and the label.

export interface OutlinedFieldProps {
  label: string;
  /** Adds the asterisk MUI's `required` draws, and nothing else — the caller
   * owns the control's `aria-required`, because only it holds the element. */
  required?: boolean;
  /**
   * Whether the label has ridden up onto the border.
   *
   * The caller's to decide, because only it knows what "has a value" means for
   * its control: text in an input, a chosen option in a select. MUI computes the
   * same thing from its own `InputBase` state.
   */
  shrink: boolean;
  /** The message under the field. Its presence is also what puts the frame in
   * its error colours. */
  error?: string;
  /** Shown under the field when there is no error — a node, as MUI's
   *  `helperText` is: the publish form's counters colour their own text. */
  helperText?: ReactNode;
  /**
   * The error colours with no message of their own — the frame, the label,
   * and whatever helper text is showing.
   *
   * For a control whose error is a *state* rather than a sentence: MUI X's
   * date field goes red on a validation error (an impossible date, a day
   * before `minDate`) with nothing to say about it (`useFieldState.mjs:126-141`),
   * so `mui-date-picker.tsx` has no string to hand `error`.
   */
  invalid?: boolean;
  /**
   * MUI's `size`: `medium` is the 56px default every form field takes;
   * `small` is the 40px outlined field (`padding: 8.5px 14px` around a 23px
   * line) the Spec list's date filters pass (`ColumnFilterPopover.tsx`,
   * `slotProps.textField.size`). Only the height and the resting label move
   * with it — the shrunk label has no size variant (`MUI_SHRUNK_FIELD_LABEL_BOX`).
   */
  size?: "small" | "medium";
  disabled?: boolean;
  /**
   * Hold the focus paint on regardless of where the DOM focus actually is.
   *
   * For the popover-backed selects, whose menu is portalled out of this frame:
   * opening one moves focus into the panel, `:focus-within` goes false, and the
   * outline drops back to the resting grey under a menu that is still open.
   * MUI never does that — `SelectInput` swallows the blur for as long as its
   * menu is open (`SelectInput.js:549-561`), so the `FormControl` stays
   * `focused` and the field keeps its 2px primary outline and blue label the
   * whole time. A plain input leaves this alone; `:focus-within` is already the
   * truth there.
   */
  focused?: boolean;
  /**
   * Which outline machinery the frame is imitating.
   *
   * `input` is MUI's `OutlinedInput` fieldset — the default, and what every
   * ported field wears.
   *
   * `hand-rolled` is the donor's **rich-text** fieldset
   * (`RichTextField.tsx:87-104`), which is a `<fieldset>` written by hand with
   * exactly three branches: 2px primary when focused, near-black when hovered,
   * else the resting 23% line. Two consequences the shared frame has to be told
   * about, because neither is MUI's:
   *
   *   - the paint follows the `focused` flag **only** — the donor sets it from
   *     a document `mousedown` landing inside the box (`:27-41`) and has no
   *     focus handler at all, so `:focus-within` must not join in;
   *   - there is **no disabled branch**, so a Description mid-save keeps the
   *     resting outline and still darkens under the pointer, where every other
   *     field on the form has gone to `action.disabled`.
   *
   * `pickers` is MUI X's `PickersOutlinedInput`: `input`'s paint exactly,
   * with the tracked legend `FieldNotch`'s `pickersTracking` describes.
   */
  outline?: "input" | "hand-rolled" | "pickers";
  /**
   * Where the focus paint comes from. `within` — the default — is
   * `:focus-within` on the frame, plus the `focused` flag.
   *
   * `prop` is the `focused` flag **alone**, for a control whose "focused" is
   * narrower than "something in the frame has the DOM focus". MUI X's date
   * field is the case: its calendar button sits inside the outline, but the
   * field's `focused` is the sections' own (`PickersInputBase.mjs:386-390`),
   * so a focused button leaves the outline at rest — measured on the running
   * donor after every close of its calendar.
   */
  focusSource?: "within" | "prop";
  /** The id of the control inside, so clicking the label focuses it. */
  /**
   * `<label for>`, for a control that is a real form element. A control that
   * is a `<button>` — the popover-backed selects — must **not** use this:
   * native label activation would forward a synthetic click to the button, and
   * the donor's floated label forwards nothing (no `InputLabel` in the source
   * carries `htmlFor`; `InputField.tsx:37-53`). Those pass `labelId` and wear
   * `aria-labelledby` on the trigger instead.
   */
  htmlFor?: string;
  /**
   * Let a press on the **floated** label activate the control, as a real
   * `<label for>` does. Off by default, because the donor's `InputField`
   * forwards nothing from its label (see `htmlFor`). On for a control whose
   * donor label *is* associated — MUI's `Autocomplete` hands the input's id to
   * its `InputLabel` (`TextField.js`, `getInputLabelProps`), so a press on the
   * floated label focuses the field there. So does MUI X's date field: its
   * shrunk label's `for` is the field's hidden input, and focusing that input
   * focuses the field (`PickersTextField.mjs:164`, `PickersInputBase.mjs:422`)
   * — clicking a floated "To date" on the donor puts the focus in its Month
   * section.
   */
  labelActivatesControl?: boolean;
  /**
   * Keep the message line when there is no message — MUI's `helperText=' '`,
   * which `FormHelperText` draws as an `aria-hidden`, `notranslate`
   * zero-width space, so the field's height never jumps as a message comes
   * and goes and the control's description stays empty.
   */
  reserveMessageLine?: boolean;
  /** Names the label element so a button control can point `aria-labelledby` at it. */
  labelId?: string;
  /** The id to give the message element, so the control can describe itself
   * with it. */
  messageId?: string;
  /**
   * How far the message sits below the field.
   *
   * `default` is MUI's own 3px. `loose` is 8px, and exists because the donor's
   * dimension fields opt out of MUI's helper-text slot and render their error
   * through a `renderError` of their own — a `FormHelperText` carrying
   * `marginTop: 1` (`timmy/src/utils/validation.ts:43`, `dimensionErrorSx`).
   * Three fields on one screen wear it; everything else keeps the 3px.
   */
  messageSpacing?: "default" | "loose";
  className?: string;
  /**
   * Classes on the frame itself — its height, where a call site's `sx`
   * overrides MUI's: the Version dropdown's medium `Select` at `height:
   * '40px!important'` (#694), which keeps the medium label and notch over a
   * 40px box.
   */
  frameClassName?: string;
  children: ReactNode;
}

export function OutlinedField({
  label,
  required = false,
  shrink,
  error,
  helperText,
  invalid = false,
  size = "medium",
  disabled = false,
  focused = false,
  outline = "input",
  focusSource = "within",
  htmlFor,
  labelActivatesControl = false,
  reserveMessageLine = false,
  labelId,
  messageId,
  messageSpacing = "default",
  className,
  frameClassName,
  children,
}: OutlinedFieldProps) {
  const message = error ?? helperText;
  const inError = invalid || Boolean(error);
  // The hand-rolled fieldset has no focus handler and no disabled branch — see
  // the `outline` prop. Both facts are the same one class-list edit away.
  const handRolled = outline === "hand-rolled";
  const focusWithin = !handRolled && focusSource === "within";
  const outlineDisabled = disabled && !handRolled;
  // **Disabled replaces the lot on the line, the width included.** MUI's
  // `FormControl` drops its focused state the moment it is disabled
  // (`FormControl.js`, `if (disabled && focused) setFocused(false)`), so the
  // focus rules stand down rather than leave 2px under the disabled colour —
  // the colour is one custom property and the width another, and only the
  // colour was ever restated.
  const outlineFocusWithin = focusWithin && !outlineDisabled;
  const outlineFocused = focused && !outlineDisabled;

  return (
    <div className={cn("w-full", className)}>
      {/* `group` so the border can answer focus *inside* it: the focus lands on
          the caller's control, and the ring belongs to the frame. */}
      {/* **The error red is `lennar-error` (#ff1744), not shadcn's
        `destructive`.** The donor never overrode `palette.error.main`, so MUI's
        `red.A400` is what its label, outline and helper text all turn — measured
        on the running donor. shadcn's `destructive` is ≈#e7000b, a visibly
        duller red, and it was showing on every ported form until the item edit
        page put it beside the out-of-range dimension tint, which is the same
        `error.main` — one field, two reds. */}
      <div className="group relative">
        <label
          id={labelId}
          htmlFor={htmlFor}
          data-shrink={shrink}
          // **A floated label swallows the click.** MUI gets this for free:
          // its `outlined && shrink` variant is `pointer-events: auto`
          // (`InputLabel.js:165-172`), the click lands on the label, and
          // nothing underneath hears it — on the running donor, clicking a
          // floated label opens no menu and takes the focus off the field.
          //
          // Atlas cannot get it for free. `PopoverTrigger asChild` clones its
          // handler onto the *frame* (`menu-select.tsx:29-32`,
          // `MenuSelectTrigger` renders this whole component, not a bare
          // button), so a click on the label still reaches the trigger by
          // bubbling. `pointer-events: auto` stops the click falling
          // *through*; only this stops it travelling *up*.
          //
          // `preventDefault` for the other half: a `<label htmlFor>` also
          // *activates* its control, so on a real `<input>` the click focused
          // the field where the donor's takes focus off it. Atlas keeps the
          // association — it is a genuine accessibility relationship, and the
          // behaviour it causes is one click on a 17px strip — and suppresses
          // the default action instead, which buys the donor's behaviour
          // without paying for it in the accessibility tree.
          //
          // Shrunk only: a resting label is `pointer-events: none` and the
          // click never touches it, which is how clicking the label text
          // inside a field opens that field.
          onClick={
            shrink && !labelActivatesControl
              ? (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }
              : undefined
          }
          className={cn(
            "absolute z-10 origin-top-left",
            // MUI's own clock, shared with the three popover-backed controls.
            MUI_FIELD_LABEL_TRANSITION,
            shrink
              ? // `translate(14px, -9px) scale(0.75)` — shared with the 40px
                // controls, because MUI's shrunk rule has no size variant. The
                // constant carries every number and where it comes from.
                cn(
                  MUI_SHRUNK_FIELD_LABEL_BOX,
                  // `userSelect: 'none'` and `pointerEvents: 'auto'` — MUI's
                  // shrunk variant overriding the outlined base's
                  // `pointer-events: none` (`InputLabel.js:153,165-172`). Up on
                  // the border the label is a surface: the donor's swallows a
                  // click rather than passing it to whatever is underneath.
                  "pointer-events-auto select-none",
                )
              : // `translate(14px, 16px) scale(1)` — the *medium* field's
                // resting spot (`InputLabel.js:146-156`), and the one piece of
                // this label that is size-dependent: a small outlined field
                // rests at `translate(14px, 9px)` instead, which is why the
                // resting box comes in two
                // (`MUI_RESTING_FIELD_LABEL_BOX_SMALL`), chosen by `size`
                // the way the frame's height below is. `leading-[1.4375]` is
                // `FormLabel`'s, unscaled here — 23px, where `leading-6` was 24
                // — and it follows `text-base` because `tailwind-merge` drops a
                // `leading-*` that precedes a conflicting `text-*`.
                cn(
                  size === "small"
                    ? MUI_RESTING_FIELD_LABEL_BOX_SMALL
                    : MUI_RESTING_FIELD_LABEL_BOX,
                  "pointer-events-none",
                ),
            // `--color-lennar-error`, not `--destructive`: MUI paints an
            // errored `FormControl` — label, border and helper text alike — in
            // `error.main`, which the Timmy theme leaves at `red.A400` #ff1744.
            // `--destructive` is a different red, and one that inverts under a
            // dark mode the donor does not have. Same call, and same reason, as
            // the out-of-range fill in `text-field.tsx` and the error paint
            // `multi-select-field.tsx` already uses.
            inError ? "text-lennar-error" : "text-lennar-ink-secondary",
            !inError && !disabled && focusWithin && "group-focus-within:text-lennar-blue-600",
            !inError && !disabled && focused && "text-lennar-blue-600",
            disabled && "text-lennar-ink-disabled",
          )}
        >
          {label}
          {required ? <RequiredAsterisk /> : null}
        </label>
        <div
          data-slot="outlined-field-frame"
          className={cn(
            // White on any ground: the donor theme's `MuiFormControl` root
            // override (`theme.ts:385-391`), which `MUI_FIELD_BASE` carries too —
            // seen on the Homesites filter bar's `grey.50` paper.
            "relative flex items-center rounded-[4px] bg-white [--mui-outline-width:1px]",
            size === "small" ? "min-h-10" : "min-h-14",
            // **The hand-rolled fieldset keeps the user agent's side margin.**
            // `InputFieldset` sets `padding` and never `margin`
            // (`RichTextField.tsx:86-104`), so `fieldset { margin: 0 2px }`
            // stands — MUI's own `NotchedOutline` zeroes it — and at `left: 0;
            // width: 100%` the donor's outline sits 2px right of its column and
            // overhangs it by 2px. Only the outline moves in the donor; the
            // text keeps its inset from the column, which `rich-text-field.tsx`
            // holds by trading 2px of its body's left padding for its right.
            handRolled && "ml-[2px] w-full",
            // **The outline is drawn, not laid out.** MUI's is an absolutely
            // positioned `fieldset`, so a 56px outlined field measures 56 — the
            // border sits *over* the box rather than adding to it. A real CSS
            // border here made every field 58, two pixels tall on every form in
            // the app. The fieldset is `FieldNotch`, the last child below; this
            // frame decides its colour and width and hands them over as
            // `--mui-outline-color` and `--mui-outline-width`
            // (`MUI_FIELD_OUTLINE`), which it inherits.
            // The four outlines, in MUI's own precedence: hover darkens the
            // resting line to near-black, focus replaces it with 2px of
            // primary, an error replaces both, and disabled replaces the lot
            // (`OutlinedInput.js:53-82`). Tailwind's `hover:` already carries
            // `@media (hover: hover)`, which is MUI's touch-device reset.
            // `focused` drops the hover rule rather than sitting after it: a
            // variant outweighs a bare utility, so a focused field under the
            // pointer would otherwise paint its *hover* outline — which is the
            // resting grey, on an open menu.
            inError
              ? cn(
                  "[--mui-outline-color:var(--color-lennar-error)]",
                  outlineFocusWithin &&
                    "focus-within:[--mui-outline-color:var(--color-lennar-error)] focus-within:[--mui-outline-width:2px]",
                  outlineFocused &&
                    "[--mui-outline-color:var(--color-lennar-error)] [--mui-outline-width:2px]",
                )
              : cn(
                  "[--mui-outline-color:var(--color-lennar-field-outline)]",
                  !outlineDisabled &&
                    !focused &&
                    (handRolled
                      ? // **The hand-rolled frame hovers on its *content*.** The
                        // donor hangs `onMouseEnter`/`onMouseLeave` on
                        // `EditorContainer` (`RichTextField.tsx:56-57`), a
                        // content-height div inside a 120px `InputContainer`
                        // (`:79-84`) — so the slack below the last line does not
                        // darken the outline, while `editorFocused` tests the
                        // whole box and *does* paint it blue when pressed. A
                        // `group-hover` over the frame darkened a band the
                        // source leaves alone.
                        "has-[[data-slot=outlined-field-body]:hover]:[--mui-outline-color:var(--color-lennar-field-outline-hover)]"
                      : // **The pointer must not un-focus the line.** MUI emits
                        // its focused colour as a variant *after* the `:hover`
                        // root rule at equal specificity, so focus beats hover
                        // (`OutlinedInput.js:53-64`) — on the running donor a
                        // focused Manufacturer keeps its 2px blue with the
                        // pointer on it. Here the two are separate Tailwind
                        // variants and `group-hover` is emitted last, so
                        // without this stand-down the pointer repainted a
                        // focused field's outline back to the resting
                        // near-black at 1px. `MUI_FIELD_BASE` carries the same
                        // fix one state along, as `not-focus-visible:`.
                        //
                        // It matters more here than it looks: the notch starts
                        // 10px in on focus because the line is two pixels, so
                        // a focused frame that dropped back to one would move
                        // the gap's ends a pixel under the pointer.
                        //
                        // **On the frame, not the group.** MUI's hover rule is
                        // the `OutlinedInput` root's own (`&:hover
                        // .notchedOutline`), and the floated label is its
                        // *sibling* — `pointer-events: auto`, so it catches the
                        // pointer, but hovering it darkens nothing. The group
                        // here holds the label, so a group hover did.
                        //
                        // `not-focus-within:hover:` rather than an arbitrary
                        // `[&:hover…]`: Tailwind's `hover:` carries the
                        // `@media (hover: hover)` guard, which is MUI's own
                        // touch reset (`OutlinedInput.js`, `@media (hover:
                        // none)` back to the resting line) — an arbitrary
                        // variant carries none, and a tap on a phone left the
                        // outline dark after the keyboard closed.
                        focusWithin
                        ? "not-focus-within:hover:[--mui-outline-color:var(--color-lennar-field-outline-hover)]"
                        : // Focus answers to the flag alone here, and the flag
                          // already stands this rule down — so a focus the
                          // flag ignores (the date picker's calendar button)
                          // still darkens under the pointer, as in MUI.
                          "hover:[--mui-outline-color:var(--color-lennar-field-outline-hover)]"),
                  // **The frame's own focus, not the group's**: every control
                  // sits inside the frame, and a `group-focus-within` also
                  // answers an *outer* field's group — so a field drawn inside
                  // another's in-place menu (the locations select's add and
                  // rename fields) wore its host's focus as its own.
                  outlineFocusWithin &&
                    "focus-within:[--mui-outline-color:var(--color-lennar-blue-600)] focus-within:[--mui-outline-width:2px]",
                  outlineFocused &&
                    "[--mui-outline-color:var(--color-lennar-blue-600)] [--mui-outline-width:2px]",
                ),
            // **Not a fade.** MUI restates a disabled field's colours and leaves
            // the ground white — the donor theme forces white on every
            // `FormControl` (`theme.ts:385-391`) — so a grey card at 60% opacity
            // is a different screen, and it is the screen every save shows.
            outlineDisabled &&
              "text-lennar-ink-disabled [--mui-outline-color:var(--color-lennar-action-disabled)]",
            frameClassName,
          )}
        >
          {children}
          {/* Last, as MUI's `renderSuffix` puts its fieldset after the input:
              it paints over the control, and the label — a `z-10` sibling of
              this frame, as `InputLabel` is `z-index: 1` beside MUI's root —
              paints over it in turn. `required` rides along because MUI cuts
              the hole for the asterisk too (`OutlinedInput.js:224-228`). */}
          <FieldNotch
            label={label}
            required={required}
            // A field with no label never opens a hole: `NotchedOutline`'s
            // `withLabel` is `label != null && label !== ''`, and without it the
            // legend is `padding: 0` at any `notched` (`NotchedOutline.js:28-41`)
            // — the Editor's label-less locations select and its inline fields.
            notched={shrink && label !== ""}
            pickersTracking={outline === "pickers"}
            // MUI's outline takes its root's radius (`NotchedOutline`'s
            // `borderRadius: 'inherit'`): 4px here by default, and whatever a
            // caller's `frameClassName` sets — the status select's 55px pill.
            className="rounded-[inherit]"
          />
        </div>
      </div>
      {message || reserveMessageLine ? (
        <p
          id={messageId}
          data-slot="outlined-field-message"
          className={cn(
            MUI_FIELD_HELPER_TEXT,
            messageSpacing === "loose" && "mt-2",
            inError ? "text-lennar-error" : "text-lennar-ink-secondary",
          )}
        >
          {message || (
            <span className="notranslate" aria-hidden="true">
              {"\u200b"}
            </span>
          )}
        </p>
      ) : null}
    </div>
  );
}
